// One station on the Floor (spec/13 §3.1, BRAND.md §4.5, §4.9, ADR-0027 §7). Three levels of loudness:
// plain means fine (a free tile has no border and no tint, just a green dot, "Free" and "Ready to play");
// a soft ember tint, a thin ember edge, the live glow and the session ring mean live; a fill means staff
// must act now (overdue is the only filled tile). Ending soon is the neutral tile with a warn ring and
// label; offline and out of order are an outline with a dimmed number. The state is a small label with a
// dot or an icon in the tile's corner, never a coloured pill, and the class is not repeated on every
// tile (the group heading already names it).

import type { FloorStationState } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { cx, SessionRing, type StationState } from '@clavian/ui-web';
import type { CSSProperties, ReactNode } from 'react';

import { bootAria, BootStrip, bootStripOf, type BootStripTone, type BootTileState } from '../boot/floor-boot';
import { Icon } from '../shell/nav-icons';
import { clockOf, isStaffRun, timeOfDay, type FloorState, type FloorStationView } from './live';
import { NowPlaying, stationNowPlaying } from './now-playing';

type ConsoleT = ReturnType<typeof useTranslation>['t'];

/** A station alert's words (F-STN-11): its kind, or a neutral fallback for a kind a newer api sends. */
export function alertWords(kind: string, t: ConsoleT): string {
  return t(`floor.alert.${kind}`, { defaultValue: t('floor.alert.unknown') });
}

/** The badge's words: the open kinds, loudest first as the api listed them. */
export function healthWords(kinds: readonly string[], t: ConsoleT): string {
  return kinds.map((kind) => alertWords(kind, t)).join(', ');
}

/** The pill a boot strip's tone reads as: its words always come with it. */
function pillForTone(tone: BootStripTone): StationState {
  switch (tone) {
    case 'ok':
      return 'free';
    case 'warn':
      return 'ending';
    case 'danger':
      return 'overdue';
    case 'info':
      return 'maintenance';
    default:
      return 'offline';
  }
}

/** The kit's pill state and the words for a floor state. A PC with no Clavian Station app is told by how it starts. */
export function pillOf(view: FloorStationView, t: ConsoleT): { readonly state: StationState; readonly words: string } {
  const left = view.secondsLeft === null ? null : clockOf(view.secondsLeft);
  switch (view.state) {
    case 'session':
      return { state: 'session', words: left === null ? t('floor.state.session') : t('floor.state.left', { time: left }) };
    case 'ending':
      return { state: 'ending', words: t('floor.state.left', { time: left ?? '0:00' }) };
    case 'overdue':
      return { state: 'overdue', words: t('floor.state.overdue') };
    case 'locked':
      return { state: 'session', words: t('floor.state.locked') };
    case 'offline':
      return { state: 'offline', words: t('floor.state.offline') };
    case 'maintenance':
      return { state: 'maintenance', words: t('floor.state.maintenance') };
    case 'disabled':
      return { state: 'offline', words: t('floor.state.disabled') };
    case 'updating':
      return { state: 'maintenance', words: t('floor.state.updating') };
    case 'settingUp':
      return { state: 'maintenance', words: t('floor.state.settingUp') };
    case 'pairing':
      return { state: 'maintenance', words: t('floor.state.pairing') };
    case 'none': {
      if (view.boot === undefined) return { state: 'maintenance', words: t('boot.bootOnly.tile.noPc') };
      const strip = bootStripOf(view.boot, t, view.now);
      return { state: pillForTone(strip.tone), words: strip.words };
    }
    default:
      return { state: 'free', words: t('floor.state.free') };
  }
}

/**
 * What a station is called to a screen reader, on its tile and in the room view: its number, name and state, then
 * who plays on it and what it runs now, where known — "Station 1, PC-01: 1:12 left. Aarav Mehta. Playing Dota 2".
 */
export function stationAria(view: FloorStationView, t: ConsoleT, playing: string | null): string {
  const name = view.station.name ?? t('floor.tile.numbered', { number: view.station.number });
  const base = t('floor.tile.aria', { number: view.station.number, name, state: pillOf(view, t).words });
  return [view.playerName, playing].reduce<string>((tile, detail) => (detail === null || detail === '' ? tile : t('floor.tile.ariaWith', { tile, detail })), base);
}

const LIVE: ReadonlySet<FloorState> = new Set(['session', 'ending', 'overdue', 'locked']);

const STATE_ICON: Partial<Record<FloorState, string>> = {
  offline: 'offline',
  disabled: 'alert',
  maintenance: 'wrench',
  updating: 'refresh',
  settingUp: 'download',
  pairing: 'qr',
};

/**
 * A PC with no Clavian Station app (`client: none`, ADR-0065): number, name, class colour and how it starts — its boot strip
 * is its state. No time ring, player, session, call or kiosk, and no "Offline" or "Pairing". Where the venue has Clavian
 * Stations too, the PC is waiting for the app and the tile says so.
 */
function ClientlessTile({
  view,
  colorToken,
  waitingForApp,
  onOpen,
}: {
  readonly view: FloorStationView;
  readonly colorToken: string | undefined;
  readonly waitingForApp: boolean;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const { station, boot } = view;
  const strip = boot === undefined ? null : bootStripOf(boot, t, view.now);
  const words = strip?.words ?? t('boot.bootOnly.tile.noPc');
  const style = colorToken === undefined ? undefined : ({ '--tile-class': `var(--clavian-${colorToken})` } as CSSProperties);
  const waiting = waitingForApp ? t('boot.bootOnly.tile.waitingForApp') : null;
  const aria = [strip?.reason ?? null, waiting].reduce<string>((tile, detail) => (detail === null ? tile : t('floor.tile.ariaWith', { tile, detail })), stationAria(view, t, null));
  return (
    <button
      type="button"
      className={cx('console-tile', 'console-tile--none', `console-tile--none-${strip?.tone ?? 'neutral'}`, boot !== undefined && 'console-tile--booted')}
      style={style}
      onClick={() => onOpen(station.stationId)}
      data-station-tile={station.stationId}
      data-boot-state={boot?.state}
      aria-label={aria}
      title={strip?.reason ?? words}
    >
      <span className="console-tile__head" aria-hidden="true">
        <span className="console-tile__number">{station.number}</span>
        <span key={words} className="console-tile__status">
          {strip?.icon == null ? <span className="console-tile__dot" /> : <Icon name={strip.icon} />}
          {words}
        </span>
      </span>
      <span className="console-tile__foot" aria-hidden="true">
        <span className="console-tile__sub">{station.name ?? ''}</span>
        {waiting === null ? null : <span className="console-tile__sub">{waiting}</span>}
        {strip?.reason == null ? null : <span className="console-tile__sub console-tile__none-reason">{strip.reason}</span>}
      </span>
    </button>
  );
}

/**
 * A PC setting up (ADR-0055): "2 of 3" — the app it is on of how many, as the PC's own screen counts — or null when the
 * station has not said (an older one, or before its first report).
 */
export function setupCount(station: Pick<FloorStationState, 'setup'>, t: ConsoleT): string | null {
  const setup = station.setup;
  if (setup === undefined || setup === null || setup.total < 1) return null;
  return t('floor.tile.setup', { current: Math.min(setup.total, setup.done + 1), total: setup.total });
}

export function FloorTile({
  view,
  colorToken,
  called = false,
  boot,
  waitingForApp = false,
  onOpen,
}: {
  readonly view: FloorStationView;
  /** A PC with no Clavian Station app in a venue that has Clavian Stations: it is waiting for the app. */
  readonly waitingForApp?: boolean;
  /** The station's class, for the screen reader's name when the station has none of its own. */
  readonly className: string | undefined;
  readonly colorToken: string | undefined;
  /** The player here has an open call for help (F-SES-25, spec/13 §3.2): the tile carries a hand flag. */
  readonly called?: boolean;
  /** How the PC starts from the boot server (Clavian Boot), when it does: the strip along the tile's foot. */
  readonly boot?: BootTileState | undefined;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const { station, state } = view;
  if (state === 'none') return <ClientlessTile view={boot === undefined ? view : { ...view, boot }} colorToken={colorToken} waitingForApp={waitingForApp} onOpen={onOpen} />;
  // One tile, one state (spec/13 §3.8, the precedence): a PC staff took out of order says so and nothing else — its boot
  // strip is not drawn, since "Ready" beside "Out of order" says two things.
  const strip = state === 'disabled' ? undefined : boot;
  const pill = pillOf(view, t);
  // In the corner the label names the state and the clock tells the time, so neither repeats the other.
  const label = state === 'session' ? t('floor.state.session') : state === 'ending' ? t('floor.state.ending') : pill.words;
  const live = LIVE.has(state);
  const until = view.session?.fundedEndAt === undefined ? null : timeOfDay(view.session.fundedEndAt ?? null);
  // Who is playing (spec/13 §3.1): on a live tile, in the line under the clock — "Until 21:30" stands in only while
  // the name is not known (a role without `players:read`); the station modal and the list keep the time.
  const player = live ? view.playerName : null;
  const style = colorToken === undefined ? undefined : ({ '--tile-class': `var(--clavian-${colorToken})` } as CSSProperties);
  const icon = STATE_ICON[state];
  const timed = live && view.secondsLeft !== null;
  // The health badge (F-STN-11): an open alert on the station. It goes when the alert clears — never dismissed by hand.
  const health = station.health;
  const unhealthy = health !== undefined && health.openAlerts > 0;
  const alerts = unhealthy ? healthWords(health.kinds, t) : '';
  // What it is running now (F-CAT-04): its own line under the clock on a live tile; in place of the quiet
  // line ("Ready to play", its name) otherwise. Nothing while offline.
  // A staff-run station has no app to say what runs (ADR-0033): the title staff picked at the start, when they picked one.
  const staffTitle = isStaffRun(station) ? view.session?.staffTitle?.name : undefined;
  const playing = staffTitle === undefined ? stationNowPlaying(station, view.now, t) : t('floor.drawer.staffRun.title', { title: staffTitle });
  const base = stationAria(view, t, playing);
  const withHealthOnly = unhealthy ? t('floor.tile.healthAria', { state: base, alerts }) : base;
  const withHealth = strip === undefined ? withHealthOnly : t('floor.tile.ariaWith', { tile: withHealthOnly, detail: bootAria(strip, t) });

  return (
    <button
      type="button"
      className={cx('console-tile', `console-tile--${state}`, called && 'console-tile--called', strip !== undefined && 'console-tile--booted')}
      style={style}
      onClick={() => onOpen(station.stationId)}
      data-station-tile={station.stationId}
      aria-label={called ? t('floor.tile.ariaCalled', { tile: withHealth }) : withHealth}
      title={playing ?? undefined}
    >
      <span className="console-tile__head" aria-hidden="true">
        <span className="console-tile__number">{station.number}</span>
        {called ? (
          <span className="console-tile__call">
            <Icon name="hand" />
            {t('floor.tile.called')}
          </span>
        ) : null}
        {unhealthy ? (
          <span
            className={cx('console-tile__health', health.severity === 'critical' && 'console-tile__health--critical')}
            title={t('floor.tile.healthBadge', { alerts })}
          >
            <Icon name="alert" />
          </span>
        ) : null}
        {/* Keyed by the state, so the label rises in again when the PC turns (ADR-0043). */}
        <span key={state} className="console-tile__status">
          {icon === undefined ? <span className="console-tile__dot" /> : <Icon name={icon} />}
          {label}
        </span>
      </span>
      <span className="console-tile__foot" aria-hidden="true">
        {timed ? (
          <span className="console-tile__time">
            <SessionRing
              value={state === 'overdue' ? 1 : view.fraction}
              tone={state === 'overdue' ? 'danger' : state === 'ending' ? 'warn' : 'session'}
              size={22}
              thickness={3}
              label={pill.words}
              className="console-tile__ring"
            />
            <span className="console-tile__clock clav-figure">{clockOf(view.secondsLeft ?? 0)}</span>
          </span>
        ) : null}
        {/* Under the clock, who is playing — or, until their name is known, the time it plays until. */}
        {player !== null ? (
          <span className="console-tile__sub console-tile__player">{player}</span>
        ) : timed ? (
          <span className="console-tile__sub">
            {state === 'overdue' ? t('floor.tile.inGrace') : until === null ? t('floor.tile.left') : t('floor.tile.until', { time: until })}
          </span>
        ) : null}
        {playing !== null ? (
          <NowPlaying text={playing} className="console-tile__playing" />
        ) : timed || player !== null ? null : (
          <span className="console-tile__sub">
            {state === 'free'
              ? t('floor.tile.ready')
              : state === 'offline' && station.lastSeenAt !== null
                ? t('floor.tile.lastSeen', { time: timeOfDay(station.lastSeenAt) ?? '' })
                : state === 'settingUp'
                  ? (setupCount(station, t) ?? station.name ?? '')
                  : (station.name ?? '')}
          </span>
        )}
        {/* How it starts from the boot server (Clavian Boot): its own line along the foot. */}
        {strip === undefined ? null : <BootStrip pc={strip} />}
      </span>
    </button>
  );
}
